import { CalendarDays, House, LogOut, Play, RefreshCw, Settings2 } from 'lucide-react';
import { useState, type CSSProperties } from 'react';

import logoUrl from '../assets/logo.png';
import { Alert } from '../components/alert';
import { useApp } from '../components/app-context';
import { Button } from '../components/button';
import { Dialog } from '../components/dialog';
import { MyMenu } from '../components/my-menu';
import { SearchBox } from '../components/search-suggestions';
import { useStore } from '../hooks/use-store';
import { t } from '../i18n';
import { HomePage } from '../pages/home.page';
import { CollectionRecordsPage } from '../pages/collection-records.page';
import { PlaybackPage } from '../pages/playback.page';
import { SchedulePage } from '../pages/schedule.page';
import { SearchPage } from '../pages/search.page';
import { SettingsPage } from '../pages/settings.page';
import { SubjectPage } from '../pages/subject.page';
import { $bangumiTokenStatus, $currentUser } from '../store/app.store';
import type { SettingsSection } from '../types/page';

export type AppRoute =
  | { view: 'home' }
  | { view: 'schedule' }
  | { view: 'collectionRecords' }
  | { view: 'settings'; section?: SettingsSection }
  | { view: 'playback' }
  | { view: 'search'; query: string }
  | { view: 'subject'; subjectId: number };

export const AppLayout = ({ route, onLogout }: { route: AppRoute; onLogout: () => void }) => {
  const { checkBangumiToken, navigate } = useApp();
  const bangumiTokenStatus = useStore($bangumiTokenStatus);
  const user = useStore($currentUser);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const active =
    route.view === 'search' || route.view === 'subject' || route.view === 'collectionRecords' ? 'home' : route.view;
  const navigation = [
    { value: 'home', label: t('home'), icon: House, path: '/' },
    { value: 'schedule', label: t('schedule'), icon: CalendarDays, path: '/schedule' },
    { value: 'playback', label: t('playback'), icon: Play, path: '/playback' },
    { value: 'settings', label: t('settings'), icon: Settings2, path: '/settings' },
  ] as const;
  const activeIndex = navigation.findIndex((item) => item.value === active);

  return (
    <div className="min-h-screen">
      <header className={`site-header ${route.view === 'playback' ? 'playback-header' : ''}`.trim()}>
        <Button variant="plain" className="brand" onClick={() => navigate('/')}>
          <div className="brand-mark small">
            <img src={logoUrl} alt="" />
          </div>
          <strong>BGMer</strong>
        </Button>
        <SearchBox />
        <nav className="has-active" style={{ '--navigation-index': activeIndex } as CSSProperties}>
          {navigation.map((item) => {
            const NavigationIcon = item.icon;
            return (
              <Button
                variant="plain"
                className={`site-nav ${item.value === active ? 'active' : ''}`}
                key={item.value}
                onClick={() => navigate(item.path)}
              >
                <NavigationIcon />
                <span>{item.label}</span>
              </Button>
            );
          })}
        </nav>
        <div className="topbar-actions flex items-center gap-2">
          <MyMenu />
          <div className="user-chip">
            <span>{user?.name}</span>
            <small>{user?.role}</small>
          </div>
          <Button variant="text" aria-label={t('logout')} onClick={() => setLogoutDialogOpen(true)}>
            <LogOut />
          </Button>
        </div>
      </header>
      <main className="content-shell">
        {bangumiTokenStatus === 'invalid' || bangumiTokenStatus === 'unavailable' ? (
          <Alert className="mb-6" variant={bangumiTokenStatus === 'invalid' ? 'error' : 'warning'}>
            <div className="flex min-w-0 flex-1 items-center justify-between gap-3 max-[700px]:flex-col max-[700px]:items-stretch">
              <span>
                {t(bangumiTokenStatus === 'invalid' ? 'bangumiTokenInvalidAlert' : 'bangumiTokenUnavailableAlert')}
              </span>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <Button compact variant="text" onClick={() => navigate('/settings/account')}>
                  <Settings2 className="size-3.5!" />
                  {t('configureBangumiToken')}
                </Button>
                <Button compact variant="text" onClick={() => void checkBangumiToken()}>
                  <RefreshCw className="size-3.5!" />
                  {t('recheckBangumiToken')}
                </Button>
              </div>
            </div>
          </Alert>
        ) : null}
        {route.view === 'home' && <HomePage />}
        {route.view === 'collectionRecords' && <CollectionRecordsPage />}
        {route.view === 'schedule' && <SchedulePage />}
        {route.view === 'search' && <SearchPage query={route.query} />}
        {route.view === 'settings' && <SettingsPage section={route.section} />}
        {route.view === 'playback' && <PlaybackPage />}
        {route.view === 'subject' && <SubjectPage subjectId={route.subjectId} />}
      </main>
      <Dialog
        open={logoutDialogOpen}
        title={t('confirmLogout')}
        description={t('confirmLogoutDescription')}
        confirmText={t('logout')}
        confirmColor="danger"
        onCancel={() => setLogoutDialogOpen(false)}
        onConfirm={() => {
          setLogoutDialogOpen(false);
          onLogout();
        }}
      />
    </div>
  );
};
